<script>
import ModelEdit from '../models/edit-v2';
import FormsModel from "@/components/forms/sales-plan.vue";
import {serialize} from 'object-to-formdata'
import {buildSalesPlanPayload} from "@/services/transformers/salesPlanPayload";
import Swal from 'sweetalert2'
import FailedToFetchModal from "@/router/views/purchase_orders/modals/FailedToFetchModal.vue";
import FailedToUpdateModal from "@/router/views/purchase_orders/modals/FailedToUpdateModal.vue";
import TimeoutRequestModal from "@/router/views/purchase_orders/modals/TimeoutRequestModal.vue";
import SuccessToUpdateModal from "@/router/views/purchase_orders/modals/SuccessToUpdateModal.vue";
import SalesPlanPublishConfirmation from "@/components/modals/sales-plan-publish-confirmation.vue";

export default {
  components: {
    SalesPlanPublishConfirmation,
    FailedToUpdateModal, TimeoutRequestModal, FailedToFetchModal, SuccessToUpdateModal},
  extends: ModelEdit,
  page() {
    return {
      title: this.lodash.startCase(this.$t('edit {something}', { something: this.$t('sales_plans.singular') })),
    };
  },
  props: {
    cardTitle: {
      default() {
        return this.lodash.startCase(this.$t('sales_plans.singular'))
      }
    },
    resourceRoute: {default: 'sales_plans'},
    modelVariable: {default: 'sales_plan'},
    form: {
      default() {
        return FormsModel;
      }
    },
    denyDelete: {type: Boolean, default: () => true},
  },
  data() {
    return {
      title: this.lodash.upperFirst(this.$t('sales_plans.plural')),
      submitAction: 'publish',
      pendingPublishValue: null,
      failedToFetchModalShow: false,
      failedToUpdateModalShow: false,
      timeoutRequestModalShow: false,
      successToUpdateModalShow: false,
      permissionChecked: false,
      publishDisabled: true,
    }
  },
  mounted() {
    this.fetchEditPermission();
  },
  computed: {
    items() {
      return [
        {
          text: this.lodash.upperFirst(this.$t('sales_plans.plural')),
          href: "/sales_plans",
        },
        {
          text: this.model && this.model.id ? this.model.id : this.$route.params.key,
          href: `/sales_plans/${this.$route.params.key}`,
          active: true,
        },
        {
          text: this.$t('Edit'),
          href: `/sales_plans/${this.$route.params.key}/edit`,
          active: true,
        },
      ]
    }
  },
  methods: {
    async fetchEditPermission() {
      this.loading = true;

      try {
        const response = await this.$axios.get(`/v1/sales-plans/${this.$route.params.key}/edit`);

        if (response?.status === 403) {
          return this.$router.replace('/403');
        }

        this.permissionChecked = true;
        await this.fetchModel();
      } catch (error) {
        if (error?.response?.status === 403) {
          return this.$router.replace('/403');
        }

        return Swal.fire({
          title: this.$t('Failed') + '!',
          text: (error.response && (error.response.data || {}).message) || error.message,
          type: 'danger',
          confirmButtonClass: 'btn btn-danger btn-fill',
          buttonsStyling: false
        }).then(() => this.$router.push('/sales_plans'));
      } finally {
        this.loading = false;
      }
    },
    async fetchModel() {
      this.loading = true;

      try {
        const response = await this.$axios.get(`/v1/sales-plans/${this.$route.params.key}`, {
          params: {
            include: [
              'partner',
              'processing',
              'product',
              'farm',
            ].join(','),
          },
          timeout: 30000,
        });
        const {data: {data}} = response;

        let processing = data.processing;
        if (data.processing_id) {
          const processingCustomerRes = await this.$axios.get(`/v1/processing-customers/${data.processing_id}`);
          const {data: {data: processingData}} = processingCustomerRes;
          processing = processingData;
        }

        this.$set(this, 'model', {
          ...data,
          processing,
          period: [data.period_start, data.period_end],
        });

        return response;
      } catch (error) {
        if (error.code === 'ECONNABORTED') {
          this.showTimeoutRequestModal()
        } else {
          this.showFailedToFetchModal()
        }
      } finally {
        this.loading = false
      }
    },
    update(value) {
      const saveAsDraft = this.submitAction === 'draft';
      return this.$axios.post(
          `/v1/sales-plans/${this.$route.params.key}`,
          serialize(
              {...buildSalesPlanPayload(value, {saveAsDraft}), _method: 'PUT'},
              {indices: true, booleansAsIntegers: true}
          ),
          {timeout: 30000}
      )
    },
    handleDraft() {
      this.submitAction = 'draft';
      const form = this.$refs.salesPlanModelEdit?.$refs?.form;
      if (!form || !('validateAll' in form)) return;

      form.validateAll({skipEstimatedSaleLimit: true}).then((isValid) => {
        if (isValid) {
          this.handleUpdate(this.model);
        }
      });
    },
    handlePublishDisabledUpdate(value) {
      this.publishDisabled = value;
    },
    handlePublishClick(handleUpdate) {
      if (this.publishDisabled) return;
      this.submitAction = 'publish';
      handleUpdate(this.model);
    },
    onValidatedUpdate(value) {
      if (this.submitAction === 'publish') {
        if (this.publishDisabled) return;
        this.pendingPublishValue = value;
        this.showPublishConfirmationModal();
      } else {
        this.handleUpdate(value);
      }
    },
    handlePublishConfirm() {
      this.handleUpdate(this.pendingPublishValue);
    },
    handleUpdate(value) {
      this.updating = true;
      this.update(value)
          .then(() => {
            this.showSuccessToUpdateModal()
            this.isEditing = false;
            setTimeout(() => this.redirectToDetailPage(), 2000)
          })
          .catch(() => this.showFailedToUpdateModal())
          .finally(() => {
            this.updating = false;
          });
    },
    requestPayload(value) {
      let detachFeedProductIds = value.feed_products
          .filter(({id}) => !value.feed_product_ids.includes(id))
          .map(({id}) => ({id, _action: 'delete'}))
      let addFeedProductIds = value.feed_product_ids.map((val) => ({id: val}))
      if (value.active_form_component === 'edit-form-after-payment') {

        return {
          hatchery_id: value.hatchery_id,
          feed_products: detachFeedProductIds.concat(addFeedProductIds),
          feed: value.feed,
          odoo_purchase_order: value.odoo_purchase_order,
          purchase_items: value.purchase_items,
          ticketable: value.ticketable,
          duration: value.duration
        }
      }

      let payload = {
        ...value,
        farm_id: value.farm_id ?? null,
        feed_products: detachFeedProductIds.concat(addFeedProductIds),
        files: value.files.filter(v => typeof v.file !== 'string').map(v => ({...v, path: v.file})),
        purchase_items: value.purchase_items.map(v => {
          return {
            ...v,
            shrimp_origin: value.shrimp_origin,
          }
        }),
        purchase_additional_items: value.additional_fees.concat(value.discounts.map(v => ({...v, total: -v.total}))),
        odoo_purchase_order: {
          ...value.odoo_purchase_order,
          odoo_vendor_id: value.vendor_selection === 'supplier' ? value.customer.odoo_partner_id : value.farmer.odoo_partner_id,
          vendor_selection: value.vendor_selection,
        }
      }
      let operationalCosts = value.other_operational_costs.map(v => ({...v, amount: v.total,}))
      let iceCost = {
        ...value.operational_costs.find(({name}) => name.toLowerCase() === 'ice'),
        name: 'ice',
        amount: value.ice_cost,
      }
      let labourCost = {
        ...value.operational_costs.find(({name}) => name.toLowerCase() === 'labour'),
        name: 'labour',
        amount: value.labour_cost,
      }
      let logisticCost = {
        ...value.operational_costs.find(({name}) => name.toLowerCase() === 'delivery'),
        name: 'delivery',
        amount: value.shipping_cost,
      }
      payload.purchase_operational_costs = operationalCosts.concat([iceCost, labourCost, logisticCost])

      if (payload.type === 'down_payment') {
        delete payload.purchase_items;
      }
      delete payload.additional_fees
      delete payload.discounts

      if (typeof payload.file === 'string') {
        delete payload.file;
      }

      const files = [];

      if (payload.invoice_documents) {
        payload.invoice_documents.map((document) => {
          files.push({...document, name: 'invoice document', path: document.file})
        })
      }
      if (payload.down_payment_documents) {
        payload.down_payment_documents.map((document) => {
          files.push({...document, name: 'down payment document', path: document.file})
        })
      }
      if (payload.operational_cost_documents) {
        payload.operational_cost_documents.map((document) => {
          files.push(({...document, name: 'operational cost document', path: document.file}))
        })
      }
      payload.files = files;
      if (value.type === 'harvest') {
        payload.total_operational_cost = value.total_operational_cost;
        delete payload.operational_costs;

      }
      return payload
    },
    delete() {
      return this.$axios.delete(`/v1/sales-plans/${this.$route.params.key}`)
    },
    showFailedToFetchModal() {
      this.$set(this, 'failedToFetchModalShow', true);
    },
    closeFailedToFetchModal() {
      this.$set(this, 'failedToFetchModalShow', false);
    },
    retryFailedToFetchModal() {
      this.closeFailedToFetchModal()
      this.fetchModel()
    },
    showFailedToUpdateModal() {
      this.$set(this, 'failedToUpdateModalShow', true);
    },
    closeFailedToUpdateModal() {
      this.$set(this, 'failedToUpdateModalShow', false);
    },
    retryFailedToUpdateModal() {
      this.closeFailedToUpdateModal()
      this.handleUpdate(this.model)
    },
    showTimeoutRequestModal() {
      this.$set(this, 'timeoutRequestModalShow', true);
    },
    closeTimeoutRequestModal() {
      this.$set(this, 'timeoutRequestModalShow', false);
    },
    retryTimeoutRequestModal() {
      this.closeTimeoutRequestModal()
      this.fetchModel()
    },
    showSuccessToUpdateModal() {
      this.$set(this, 'successToUpdateModalShow', true);
    },
    redirectToDashboard() {
      this.$router.push('/')
    },
    redirectToDetailPage() {
      this.$router.push(`/sales_plans/${this.$route.params.key}`)
    },
    showPublishConfirmationModal() {
      const modalRef = this.$refs["sales-plan-publish-confirmation"]
      const title = this.$t('sales_plans.modal_messages.confirm_publish_title')
      const message = this.$t('sales_plans.modal_messages.confirm_publish_message')
      if (!modalRef) {
        return Swal.fire({
          title: title + "!",
          text: message,
          type: "success",
          confirmButtonClass: "btn btn-success btn-fill",
          buttonsStyling: false
        });
      }

      return modalRef.show({
        title: title,
        message,
      });
    }
  }
}
</script>
<template>
  <Layout>
    <PageHeader :items="items" :title="title"/>
      <failed-to-fetch-modal
          v-model="failedToFetchModalShow"
          :model-id="$route.params.key"
          :name="$t('sales_plans.singular')"
          @back="handleBack"
          @retry="retryFailedToFetchModal"
      />
      <failed-to-update-modal
          v-model="failedToUpdateModalShow"
          :model-id="$route.params.key"
          :name="$t('sales_plans.singular')"
          @back="handleBack"
          @retry="retryFailedToUpdateModal"
      />
      <success-to-update-modal
          v-model="successToUpdateModalShow"
          :model-id="$route.params.key"
          :name="$t('sales_plans.singular')"
          @back-to-dashboard="redirectToDashboard"
          @view-detail-page="redirectToDetailPage"
      />
    <sales-plan-publish-confirmation
        ref="sales-plan-publish-confirmation"
        @confirm="handlePublishConfirm"
    />
    <timeout-request-modal
        v-model="timeoutRequestModalShow"
        @back="handleBack"
        @retry="retryTimeoutRequestModal"
    />
    <div v-if="permissionChecked" class="row">
      <model-edit
          ref="salesPlanModelEdit"
          :key="getModelKey()"
          v-model="model"
          :deleting="deleting"
          :errors.sync="errors"
          :loading="loading"
          :updating="updating"
          class="col-12"
          v-bind="$props"
          :form-listeners="{ 'update:publish-disabled': handlePublishDisabledUpdate }"
          @back-click="handleBack"
          @update-click="onValidatedUpdate"
          @delete-click="handleDelete"
          @model-changed="handleModelChanged">
        <template #card-footer="{ handleBack, handleUpdate, updating }">
          <div class="row">
            <div class="col-12 d-flex flex-row-reverse justify-content-start flex-wrap">
              <b-button :disabled="updating || publishDisabled" class="ms-2" native-type="submit" variant="primary" @click="handlePublishClick(handleUpdate)">
                <i v-if="updating" class="bx bx-loader bx-spin font-size-16 align-middle me-2"></i>
                {{ lodash.startCase($t('publish')) }}
              </b-button>
              <b-button :disabled="updating" class="ms-2" native-type="submit" variant="warning" @click="handleDraft">
                <i v-if="updating" class="bx bx-loader bx-spin font-size-16 align-middle me-2"></i>
                {{ `${$t('Update')} ${lodash.startCase($t('draft'))}` }}
              </b-button>
              <b-button class="ms-2" variant="link" @click="handleBack">
                {{ $t('Back') }}
              </b-button>
            </div>
          </div>
        </template>
      </model-edit>
    </div>
  </Layout>
</template>
